import { useState } from "react";

import { translate } from "app/filters/i18nFilter";
import type { IModifiablePrompt } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/types";
import { allTrackers } from "app/services/track/track";

import type { ISimpleWarningModalHook } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/useSimpleWarningModal";
import { useSimpleWarningModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/useSimpleWarningModal";

interface IDeletePromptsModalHook extends Pick<ISimpleWarningModalHook, "modal"> {
    openModal: (prompts: IModifiablePrompt[]) => void;
}

export const useDeletePromptsModal = (onDelete: (prompts: IModifiablePrompt[]) => void): IDeletePromptsModalHook => {
    const [promptsToDelete, setPromptsToDelete] = useState<IModifiablePrompt[]>([]);
    const isBulk = promptsToDelete.length > 1;

    const resetState = (): void => {
        setPromptsToDelete([]);
    };

    const onCancel = (): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "delete prompt/cancel");
    };

    const onSubmit = (): void => {
        if (!promptsToDelete?.length) {
            return;
        }

        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "delete prompt/submit", {
            count: promptsToDelete.length,
        });
        onDelete(promptsToDelete);
    };

    const text = isBulk
        ? translate("llm.prompts.edit.prompts-table.bulk-delete.text", { count: promptsToDelete.length })
        : translate("llm.prompts.edit.prompts-table.delete.text", { prompt: promptsToDelete[0]?.query });

    const { openModal: openGenericModal, modal } = useSimpleWarningModal({
        title: translate(
            isBulk ? "llm.prompts.edit.prompts-table.bulk-delete.title" : "llm.prompts.edit.prompts-table.delete.title",
        ),
        tooltip: translate(
            isBulk
                ? "llm.prompts.edit.prompts-table.bulk-delete.tooltip"
                : "llm.prompts.edit.prompts-table.delete.tooltip",
        ),
        ctaText: translate("common.delete"),
        text,
        onCancel,
        resetState,
        onSubmit,
    });

    const openModal = (prompts: IModifiablePrompt[]): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "delete prompt/open");
        setPromptsToDelete(prompts);
        openGenericModal();
    };

    return {
        openModal,
        modal,
    };
};
